import { buildHref, HrefOptions } from "@/lib/navigation";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";

export type AppNavigateOptions = HrefOptions | string;

export const APP_HISTORY_NAVIGATION_EVENT = "app:history-navigation";

export function normalizeComparableHref(href: string, origin: string) {
  try {
    const url = new URL(href, origin);
    if (url.origin !== origin) return null;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href || "/";
  }
}

export function isSamePathNavigation(currentHref: string, nextHref: string | null) {
  if (!nextHref) return false;

  const [currentPath] = currentHref.split(/[?#]/);
  const [nextPath] = nextHref.split(/[?#]/);
  return currentPath === nextPath;
}

function updateBrowserHistory(href: string, replace?: boolean, state?: unknown) {
  const historyState = typeof state === "undefined" ? window.history.state : state;
  if (replace) {
    window.history.replaceState(historyState, "", href);
  } else {
    window.history.pushState(historyState, "", href);
  }
  window.dispatchEvent(new Event(APP_HISTORY_NAVIGATION_EVENT));
}

export const useAppNavigate = () => {
  const router = useRouter();
  const pathname = usePathname();
  const currentSearchParams = useSearchParams();
  const params = useParams();
  // 从路由参数读 locale，比 window.location 更可靠（SSR 安全）
  const locale = typeof params?.locale === "string" ? params.locale : undefined;

  return useCallback(
    (options: AppNavigateOptions) => {
      // number -1 语义改为直接调用 router.back()
      if (typeof options !== "string" && options.back) {
        router.back();
        return;
      }

      // 只有 search 是函数形式时才需要当前 query；浏览器端优先读 window，兼容同路由 shallow 更新后的最新 search。
      const needsCurrentParams = typeof options !== "string" && typeof options.search === "function";
      const scroll = typeof options !== "string" ? options.scroll : undefined;
      const browserSearchParams =
        typeof window === "undefined" ? undefined : new URLSearchParams(window.location.search);

      const href = buildHref(options, needsCurrentParams ? (browserSearchParams ?? currentSearchParams) : undefined, locale);

      if (typeof href === "number") {
        router.back();
        return;
      }

      const currentHref =
        typeof window === "undefined"
          ? `${pathname}${currentSearchParams.toString() ? `?${currentSearchParams.toString()}` : ""}`
          : `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextHref = normalizeComparableHref(href, typeof window === "undefined" ? "https://local.invalid" : window.location.origin);

      if (nextHref === currentHref) {
        return;
      }

      if (typeof window !== "undefined" && isSamePathNavigation(currentHref, nextHref)) {
        updateBrowserHistory(href, typeof options !== "string" && options.replace, typeof options !== "string" ? options.state : undefined);
        return;
      }

      if (typeof options !== "string" && options.replace) {
        router.replace(href, { scroll });
        return;
      }
      router.push(href, { scroll });
    },
    [router, pathname, currentSearchParams, locale],
  );
};

export const useAppPrefetch = () => {
  const router = useRouter();
  const currentSearchParams = useSearchParams();
  const params = useParams();
  const locale = typeof params?.locale === "string" ? params.locale : undefined;

  return useCallback(
    (options: AppNavigateOptions) => {
      if (typeof options !== "string" && options.back) return;

      const needsCurrentParams = typeof options !== "string" && typeof options.search === "function";
      const browserSearchParams =
        typeof window === "undefined" ? undefined : new URLSearchParams(window.location.search);
      const href = buildHref(options, needsCurrentParams ? (browserSearchParams ?? currentSearchParams) : undefined, locale);

      if (typeof href !== "string") return;
      router.prefetch(href);
    },
    [router, currentSearchParams, locale],
  );
};
